import React, { useEffect, useState, Children } from 'react';
import { ArrowLeft, Check, CreditCard, Landmark, ChevronDown, HelpCircle, X, AlignLeft, AlignCenter, Palette, Layout, Sliders } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function PaymentModal() {
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'bacs'>('bacs');
  const [showFeeEditor, setShowFeeEditor] = useState(false);
  const [feePercentage, setFeePercentage] = useState(4.5);
  const [feeEditorVersion, setFeeEditorVersion] = useState<2 | 3 | 4>(2);
  const [alignment, setAlignment] = useState<'left' | 'center'>('left');
  const [brandColor, setBrandColor] = useState('#ccff00');
  const [secondaryColor, setSecondaryColor] = useState('#059669');
  const [keepFeeLineVisible, setKeepFeeLineVisible] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isControlsExpanded, setIsControlsExpanded] = useState(true);
  const baseAmount = 50.0;
  const processingFee = baseAmount * feePercentage / 100;
  const totalAmount = baseAmount + processingFee;
  const feeOptions = [{
    percentage: 0,
    amount: 0,
    description: 'Standard processing'
  }, {
    percentage: 1.5,
    amount: baseAmount * 1.5 / 100,
    description: 'Help cover costs'
  }, {
    percentage: 3,
    amount: baseAmount * 3 / 100,
    description: 'Support our mission'
  }, {
    percentage: 4.5,
    amount: baseAmount * 4.5 / 100,
    description: 'Full support'
  }];
  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const index = parseInt(e.target.value);
    setFeePercentage(feeOptions[index].percentage);
  };
  const getCurrentSliderIndex = () => {
    return feeOptions.findIndex(opt => opt.percentage === feePercentage);
  };
  const handleDonate = async () => {
    setIsProcessing(true);
    // Simulate processing
    await new Promise(resolve => setTimeout(resolve, 2000));
    setIsProcessing(false);
  };
  // Keyboard shortcut to close fee editor
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showFeeEditor) {
        setShowFeeEditor(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showFeeEditor]);
  return <div className="relative w-full max-w-[480px] mx-auto bg-white rounded-3xl shadow-2xl overflow-visible my-8" data-id="element-6">
      {/* Enhanced Settings Panel */}
      <motion.div initial={{
      opacity: 0,
      x: -20
    }} animate={{
      opacity: 1,
      x: 0
    }} className="fixed bottom-4 left-4 z-50 bg-white rounded-2xl shadow-xl border border-gray-200 w-[260px] overflow-hidden" data-id="element-7">
        <div className="bg-gradient-to-br from-gray-50 to-white p-4 border-b border-gray-200" data-id="element-8">
          <div className="flex items-center justify-between mb-1" data-id="element-9">
            <div className="flex items-center gap-2" data-id="element-10">
              <Sliders size={14} className="text-gray-600" data-id="element-11" />
              <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider" data-id="element-12">
                Design Controls
              </h3>
            </div>
            <button onClick={() => setIsControlsExpanded(!isControlsExpanded)} className="text-gray-600 hover:text-gray-900 transition-colors p-1 hover:bg-gray-100 rounded-lg" aria-label={isControlsExpanded ? 'Collapse controls' : 'Expand controls'} data-id="element-13">
              <motion.div animate={{
              rotate: isControlsExpanded ? 0 : 180
            }} transition={{
              duration: 0.2
            }} data-id="element-14">
                <ChevronDown size={16} data-id="element-15" />
              </motion.div>
            </button>
          </div>
        </div>

        <AnimatePresence initial={false} data-id="element-16">
          {isControlsExpanded && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeInOut'
        }} className="overflow-hidden" data-id="element-17">
              <div className="p-4 space-y-4" data-id="element-18">
                {/* Alignment Section */}
                <div data-id="element-19">
                  <div className="flex items-center gap-1.5 mb-2" data-id="element-20">
                    <Layout size={12} className="text-gray-500" data-id="element-21" />
                    <div className="text-[10px] font-semibold text-gray-700 uppercase tracking-wider" data-id="element-22">
                      Layout
                    </div>
                  </div>
                  <div className="flex gap-1.5 bg-gray-100 rounded-xl p-1" data-id="element-23">
                    <button onClick={() => setAlignment('left')} className={`flex-1 py-2 text-xs font-medium rounded-lg transition-all flex items-center justify-center gap-1.5 ${alignment === 'left' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-24">
                      <AlignLeft size={12} data-id="element-25" />
                      Left
                    </button>
                    <button onClick={() => setAlignment('center')} className={`flex-1 py-2 text-xs font-medium rounded-lg transition-all flex items-center justify-center gap-1.5 ${alignment === 'center' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-26">
                      <AlignCenter size={12} data-id="element-27" />
                      Center
                    </button>
                  </div>
                </div>

                {/* Colors Section */}
                <div data-id="element-28">
                  <div className="flex items-center gap-1.5 mb-2" data-id="element-29">
                    <Palette size={12} className="text-gray-500" data-id="element-30" />
                    <div className="text-[10px] font-semibold text-gray-700 uppercase tracking-wider" data-id="element-31">
                      Brand Colors
                    </div>
                  </div>
                  <div className="space-y-2.5" data-id="element-32">
                    <div className="flex items-center gap-2" data-id="element-33">
                      <label className="text-xs text-gray-600 w-20 shrink-0 font-medium" data-id="element-34">
                        Brand
                      </label>
                      <input type="color" value={brandColor} onChange={e => setBrandColor(e.target.value)} className="w-9 h-9 rounded-lg border-2 border-gray-200 cursor-pointer shrink-0 hover:border-gray-300 transition-colors" data-id="element-35" />
                      <input type="text" value={brandColor} onChange={e => setBrandColor(e.target.value)} className="w-[75px] px-2.5 py-1.5 text-xs font-mono border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent" data-id="element-36" />
                    </div>
                    <div className="flex items-center gap-2" data-id="element-37">
                      <label className="text-xs text-gray-600 w-20 shrink-0 font-medium" data-id="element-38">
                        Secondary
                      </label>
                      <input type="color" value={secondaryColor} onChange={e => setSecondaryColor(e.target.value)} className="w-9 h-9 rounded-lg border-2 border-gray-200 cursor-pointer shrink-0 hover:border-gray-300 transition-colors" data-id="element-39" />
                      <input type="text" value={secondaryColor} onChange={e => setSecondaryColor(e.target.value)} className="w-[75px] px-2.5 py-1.5 text-xs font-mono border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent" data-id="element-40" />
                    </div>
                  </div>
                </div>

                {/* Fee Display Section */}
                <div data-id="element-41">
                  <div className="text-[10px] font-semibold text-gray-700 uppercase tracking-wider mb-2" data-id="element-42">
                    Fee Visibility
                  </div>
                  <div className="flex gap-1.5 bg-gray-100 rounded-xl p-1" data-id="element-43">
                    <button onClick={() => setKeepFeeLineVisible(false)} className={`flex-1 py-2 text-xs font-medium rounded-lg transition-all ${!keepFeeLineVisible ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-44">
                      Hide on edit
                    </button>
                    <button onClick={() => setKeepFeeLineVisible(true)} className={`flex-1 py-2 text-xs font-medium rounded-lg transition-all ${keepFeeLineVisible ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-45">
                      Always show
                    </button>
                  </div>
                </div>

                {/* Fee Editor Version Section */}
                <div data-id="element-46">
                  <div className="text-[10px] font-semibold text-gray-700 uppercase tracking-wider mb-2" data-id="element-47">
                    Fee Editor Style
                  </div>
                  <div className="flex gap-1.5 bg-gray-100 rounded-xl p-1" data-id="element-48">
                    <button onClick={() => setFeeEditorVersion(2)} className={`flex-1 py-2 text-xs font-medium rounded-lg transition-all ${feeEditorVersion === 2 ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-49">
                      Slider
                    </button>
                    <button onClick={() => setFeeEditorVersion(3)} className={`flex-1 py-2 text-xs font-medium rounded-lg transition-all ${feeEditorVersion === 3 ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-50">
                      Buttons
                    </button>
                    <button onClick={() => setFeeEditorVersion(4)} className={`flex-1 py-2 text-xs font-medium rounded-lg transition-all ${feeEditorVersion === 4 ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-51">
                      Dropdown
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </motion.div>

      {/* Top Badge */}
      <motion.div initial={{
      scale: 0.8,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} transition={{
      type: 'spring',
      stiffness: 200,
      damping: 15
    }} className="absolute -top-8 left-0 right-0 mx-auto w-20 z-20" data-id="element-52">
        <div className="bg-[#006855] text-white w-20 h-20 rounded-full flex flex-col items-center justify-center text-xs font-bold leading-tight shadow-lg border-4 border-white" data-id="element-53">
          <span data-id="element-54">SAVE</span>
          <span data-id="element-55">THE</span>
          <span data-id="element-56">TREES</span>
        </div>
      </motion.div>

      {/* Close Button */}
      <button className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10 p-1 hover:bg-gray-100 rounded-full" data-id="element-57">
        <X size={20} data-id="element-58" />
      </button>

      {/* Header Section */}
      <div className="pt-16 pb-4 px-8" data-id="element-59">
        <div className="flex items-center justify-between mb-8" data-id="element-60">
          <button className="flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors" data-id="element-61">
            <ArrowLeft size={16} className="mr-1" data-id="element-62" />
            Back
          </button>
          {/* Progress Steps */}
          <div className="flex space-x-2" data-id="element-63">
            {[1, 2, 3].map(step => <motion.div key={step} initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            delay: step * 0.1
          }} className="w-5 h-5 rounded-full flex items-center justify-center" style={{
            backgroundColor: secondaryColor
          }} data-id="element-64">
                <Check size={12} className="text-white" strokeWidth={4} data-id="element-65" />
              </motion.div>)}
            <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            delay: 0.4
          }} className="w-5 h-5 rounded-full flex items-center justify-center" style={{
            backgroundColor: secondaryColor
          }} data-id="element-66">
              <div className="w-2 h-2 bg-white rounded-full" data-id="element-67" />
            </motion.div>
          </div>
          <div className="w-12" data-id="element-68" />
        </div>

        <h1 className="text-2xl font-bold text-gray-900 mb-6 text-center" data-id="element-69">
          Select payment method
        </h1>

        {/* Secure Payment Banner - Standalone */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="flex items-start gap-4 mb-8 bg-white rounded-xl p-4 shadow-sm border border-gray-200" data-id="element-70">
          <div className="bg-blue-50 p-2.5 rounded-xl shadow-sm shrink-0 relative" data-id="element-71">
            <div className="text-blue-600" data-id="element-72">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-73">
                <path d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z" fill="currentColor" data-id="element-74" />
                <path d="M12 8V11" stroke="white" strokeWidth="2" strokeLinecap="round" data-id="element-75" />
                <circle cx="12" cy="14" r="1.5" fill="white" data-id="element-76" />
              </svg>
            </div>
            <div className="absolute -bottom-1 -right-1 bg-emerald-600 rounded-full p-0.5 border-2 border-white" data-id="element-77">
              <Check size={8} className="text-white" strokeWidth={3} data-id="element-78" />
            </div>
          </div>
          <div data-id="element-79">
            <h3 className="font-bold text-sm text-gray-900 mb-0.5" data-id="element-80">
              Secure payment
            </h3>
            <p className="text-xs text-gray-600 leading-relaxed" data-id="element-81">
              Our payments are protected by industry best-practice encryption
              technology.
            </p>
          </div>
        </motion.div>

        {/* Express Checkout Section */}
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 mb-8 border border-gray-200" data-id="element-82">
          {/* Express Checkout Divider */}
          <div className="relative mb-6" data-id="element-83">
            <div className="absolute inset-0 flex items-center" aria-hidden="true" data-id="element-84">
              <div className="w-full border-t border-gray-300" data-id="element-85"></div>
            </div>
            <div className="relative flex justify-center" data-id="element-86">
              <span className="bg-gradient-to-br from-gray-50 to-gray-100 px-3 text-xs text-gray-500 uppercase tracking-wider font-medium" data-id="element-87">
                Express checkout
              </span>
            </div>
          </div>

          <motion.div initial="hidden" animate="visible" variants={{
          visible: {
            transition: {
              staggerChildren: 0.1
            }
          }
        }} className="space-y-3" data-id="element-88">
            {/* PayPal Button */}
            <motion.button variants={{
            hidden: {
              opacity: 0,
              y: 10
            },
            visible: {
              opacity: 1,
              y: 0
            }
          }} whileHover={{
            scale: 1.01
          }} whileTap={{
            scale: 0.99
          }} className="w-full bg-[#FFC439] hover:bg-[#F4B830] h-12 rounded-xl flex items-center justify-center transition-all shadow-sm hover:shadow" data-id="element-89">
              <svg viewBox="0 0 124 33" className="h-7" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-90">
                <path d="M46.211 6.749h-6.839a.95.95 0 0 0-.939.802l-2.766 17.537a.57.57 0 0 0 .564.658h3.265a.95.95 0 0 0 .939-.803l.746-4.73a.95.95 0 0 1 .938-.803h2.165c4.505 0 7.105-2.18 7.784-6.5.306-1.89.013-3.375-.872-4.415-.972-1.142-2.696-1.746-4.985-1.746zm.789 6.405c-.374 2.454-2.249 2.454-4.062 2.454h-1.032l.724-4.583a.57.57 0 0 1 .563-.481h.473c1.235 0 2.4 0 3.002.704.359.42.469 1.044.332 1.906zM66.654 13.075h-3.275a.57.57 0 0 0-.563.481l-.145.916-.229-.332c-.709-1.029-2.29-1.373-3.868-1.373-3.619 0-6.71 2.741-7.312 6.586-.313 1.918.132 3.752 1.22 5.031.998 1.176 2.426 1.666 4.125 1.666 2.916 0 4.533-1.875 4.533-1.875l-.146.91a.57.57 0 0 0 .562.66h2.95a.95.95 0 0 0 .939-.803l1.77-11.209a.568.568 0 0 0-.561-.658zm-4.565 6.374c-.316 1.871-1.801 3.127-3.695 3.127-.951 0-1.711-.305-2.199-.883-.484-.574-.668-1.391-.514-2.301.295-1.855 1.805-3.152 3.67-3.152.93 0 1.686.309 2.184.892.499.589.697 1.411.554 2.317zM84.096 13.075h-3.291a.954.954 0 0 0-.787.417l-4.539 6.686-1.924-6.425a.953.953 0 0 0-.912-.678h-3.234a.57.57 0 0 0-.541.754l3.625 10.638-3.408 4.811a.57.57 0 0 0 .465.9h3.287a.949.949 0 0 0 .781-.408l10.946-15.8a.57.57 0 0 0-.468-.895z" fill="#253B80" data-id="element-91" />
                <path d="M94.992 6.749h-6.84a.95.95 0 0 0-.938.802l-2.766 17.537a.569.569 0 0 0 .562.658h3.51a.665.665 0 0 0 .656-.562l.785-4.971a.95.95 0 0 1 .938-.803h2.164c4.506 0 7.105-2.18 7.785-6.5.307-1.89.012-3.375-.873-4.415-.971-1.142-2.694-1.746-4.983-1.746zm.789 6.405c-.373 2.454-2.248 2.454-4.062 2.454h-1.031l.725-4.583a.568.568 0 0 1 .562-.481h.473c1.234 0 2.4 0 3.002.704.359.42.468 1.044.331 1.906zM115.434 13.075h-3.273a.567.567 0 0 0-.562.481l-.145.916-.23-.332c-.709-1.029-2.289-1.373-3.867-1.373-3.619 0-6.709 2.741-7.311 6.586-.312 1.918.131 3.752 1.219 5.031 1 1.176 2.426 1.666 4.125 1.666 2.916 0 4.533-1.875 4.533-1.875l-.146.91a.57.57 0 0 0 .564.66h2.949a.95.95 0 0 0 .938-.803l1.771-11.209a.571.571 0 0 0-.565-.658zm-4.565 6.374c-.314 1.871-1.801 3.127-3.695 3.127-.949 0-1.711-.305-2.199-.883-.484-.574-.666-1.391-.514-2.301.297-1.855 1.805-3.152 3.67-3.152.93 0 1.686.309 2.184.892.501.589.699 1.411.554 2.317zM119.295 7.23l-2.807 17.858a.569.569 0 0 0 .562.658h2.822c.469 0 .867-.34.939-.803l2.768-17.536a.57.57 0 0 0-.562-.659h-3.16a.571.571 0 0 0-.562.482z" fill="#179BD7" data-id="element-92" />
                <path d="M7.266 29.154l.523-3.322-1.165-.027H1.061L4.927 1.292a.316.316 0 0 1 .314-.268h9.38c3.114 0 5.263.648 6.385 1.927.526.6.861 1.227 1.023 1.917.17.724.173 1.589.007 2.644l-.012.077v.676l.526.298a3.69 3.69 0 0 1 1.065.812c.45.513.741 1.165.864 1.938.127.795.085 1.741-.123 2.812-.24 1.232-.628 2.305-1.152 3.183a6.547 6.547 0 0 1-1.825 2c-.696.494-1.523.869-2.458 1.109-.906.236-1.939.355-3.072.355h-.73c-.522 0-1.029.188-1.427.525a2.21 2.21 0 0 0-.744 1.328l-.055.299-.924 5.855-.042.215c-.011.068-.03.102-.058.125a.155.155 0 0 1-.096.035H7.266z" fill="#253B80" data-id="element-93" />
                <path d="M23.048 7.667c-.028.179-.06.362-.096.55-1.237 6.351-5.469 8.545-10.874 8.545H9.326c-.661 0-1.218.48-1.321 1.132L6.596 26.83l-.399 2.533a.704.704 0 0 0 .695.814h4.881c.578 0 1.069-.42 1.16-.99l.048-.248.919-5.832.059-.32c.09-.572.582-.992 1.16-.992h.73c4.729 0 8.431-1.92 9.513-7.476.452-2.321.218-4.259-.978-5.622a4.667 4.667 0 0 0-1.336-1.03z" fill="#179BD7" data-id="element-94" />
                <path d="M21.754 7.151a9.757 9.757 0 0 0-1.203-.267 15.284 15.284 0 0 0-2.426-.177h-7.352a1.172 1.172 0 0 0-1.159.992L8.05 17.605l-.045.289a1.336 1.336 0 0 1 1.321-1.132h2.752c5.405 0 9.637-2.195 10.874-8.545.037-.188.068-.371.096-.55a6.594 6.594 0 0 0-1.017-.429 9.045 9.045 0 0 0-.277-.087z" fill="#222D65" data-id="element-95" />
                <path d="M9.614 7.699a1.169 1.169 0 0 1 1.159-.991h7.352c.871 0 1.684.057 2.426.177a9.757 9.757 0 0 1 1.481.353c.365.121.704.264 1.017.429.368-2.347-.003-3.945-1.272-5.392C20.378.682 17.853 0 14.622 0h-9.38c-.66 0-1.223.48-1.325 1.133L.01 25.898a.806.806 0 0 0 .795.932h5.791l1.454-9.225 1.564-9.906z" fill="#253B80" data-id="element-96" />
              </svg>
            </motion.button>

            {/* Google Pay Button */}
            <motion.button variants={{
            hidden: {
              opacity: 0,
              y: 10
            },
            visible: {
              opacity: 1,
              y: 0
            }
          }} whileHover={{
            scale: 1.01
          }} whileTap={{
            scale: 0.99
          }} className="w-full bg-black hover:bg-gray-900 h-12 rounded-xl flex items-center justify-center transition-all text-white font-medium text-lg shadow-sm hover:shadow" data-id="element-97">
              <div className="flex items-center gap-1.5" data-id="element-98">
                <svg className="h-5 w-auto" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-99">
                  <path d="M23.49 12.275C23.49 11.49 23.415 10.73 23.3 10H12V14.51H18.47C18.18 15.99 17.25 17.21 15.81 18.11V21.09H19.62C21.85 19.09 23.49 16.04 23.49 12.275Z" fill="#4285F4" data-id="element-100" />
                  <path d="M12 24C15.24 24 17.965 22.935 19.965 21.09L16.155 18.11C15.075 18.82 13.68 19.25 12 19.25C8.865 19.25 6.21 17.14 5.265 14.29H1.305V17.385C3.375 21.41 7.5 24 12 24Z" fill="#34A853" data-id="element-101" />
                  <path d="M5.265 14.29C5.025 13.57 4.905 12.805 4.905 12C4.905 11.195 5.025 10.43 5.265 9.71V6.615H1.305C0.465 8.235 0 10.06 0 12C0 13.94 0.465 15.765 1.305 17.385L5.265 14.29Z" fill="#FBBC05" data-id="element-102" />
                  <path d="M12 4.75C13.77 4.75 15.345 5.36 16.59 6.55L20.025 3.115C17.95 1.19 15.225 0 12 0C7.5 0 3.375 2.59 1.305 6.615L5.265 9.71C6.21 6.86 8.865 4.75 12 4.75Z" fill="#EA4335" data-id="element-103" />
                </svg>
                <span data-id="element-104">Pay</span>
              </div>
            </motion.button>

            {/* Apple Pay Button */}
            <motion.button variants={{
            hidden: {
              opacity: 0,
              y: 10
            },
            visible: {
              opacity: 1,
              y: 0
            }
          }} whileHover={{
            scale: 1.01
          }} whileTap={{
            scale: 0.99
          }} className="w-full bg-black hover:bg-gray-900 h-12 rounded-xl flex items-center justify-center transition-all shadow-sm hover:shadow" data-id="element-105">
              <div className="flex items-center gap-1" data-id="element-106">
                <svg className="h-6 w-6" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg" data-id="element-107">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" data-id="element-108" />
                </svg>
                <span className="text-white text-xl font-medium" data-id="element-109">Pay</span>
              </div>
            </motion.button>
          </motion.div>
        </div>

        {/* OR Divider with Enter Payment Details */}
        <div className="mb-8" data-id="element-110">
          <div className="relative mb-6" data-id="element-111">
            <div className="absolute inset-0 flex items-center" aria-hidden="true" data-id="element-112">
              <div className="w-full border-t border-gray-300" data-id="element-113"></div>
            </div>
            <div className="relative flex justify-center" data-id="element-114">
              <span className="bg-white px-4 text-xs text-gray-500 uppercase tracking-wider font-medium" data-id="element-115">
                OR
              </span>
            </div>
          </div>

          <p className="text-gray-600 text-sm text-center" data-id="element-116">
            Enter your payment details
          </p>
        </div>

        {/* Payment Form Section */}
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 mb-8 border border-gray-200" data-id="element-117">
          {/* Payment Type Tabs */}
          <div className="flex gap-3 mb-6" data-id="element-118">
            <motion.button whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} onClick={() => setPaymentMethod('card')} className={`flex-1 py-4 px-4 rounded-xl flex flex-col items-start transition-all ${paymentMethod === 'card' ? 'border-2 border-gray-900 bg-white shadow-md' : 'border-2 border-transparent bg-white hover:border-gray-300 shadow-sm'}`} data-id="element-119">
              <CreditCard className={`w-6 h-6 mb-2 ${paymentMethod === 'card' ? 'text-gray-900' : 'text-gray-400'}`} data-id="element-120" />
              <span className={`text-sm ${paymentMethod === 'card' ? 'font-bold text-gray-900' : 'font-medium text-gray-700'}`} data-id="element-121">
                Card
              </span>
            </motion.button>
            <motion.button whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} onClick={() => setPaymentMethod('bacs')} className={`flex-1 py-4 px-4 rounded-xl flex flex-col items-start transition-all ${paymentMethod === 'bacs' ? 'border-2 border-gray-900 bg-white shadow-md' : 'border-2 border-transparent bg-white hover:border-gray-300 shadow-sm'}`} data-id="element-122">
              <Landmark className={`w-6 h-6 mb-2 ${paymentMethod === 'bacs' ? 'text-gray-900' : 'text-gray-400'}`} data-id="element-123" />
              <span className={`text-sm ${paymentMethod === 'bacs' ? 'font-bold text-gray-900' : 'font-medium text-gray-700'}`} data-id="element-124">
                Bank Account
              </span>
            </motion.button>
          </div>

          {/* Form Fields with Animation */}
          <AnimatePresence mode="wait" data-id="element-125">
            <motion.div key={paymentMethod} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.2
          }} className="space-y-4" data-id="element-126">
              {paymentMethod === 'card' ? <>
                  <div data-id="element-127">
                    <label className="block text-xs font-semibold text-gray-700 mb-2" data-id="element-128">
                      Card number
                    </label>
                    <input type="text" placeholder="1234 5678 9012 3456" className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-900 text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all placeholder-gray-400" data-id="element-129" />
                  </div>

                  <div className="grid grid-cols-2 gap-4" data-id="element-130">
                    <div data-id="element-131">
                      <label className="block text-xs font-semibold text-gray-700 mb-2" data-id="element-132">
                        Expiry date
                      </label>
                      <input type="text" placeholder="MM / YY" className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-900 text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all placeholder-gray-400" data-id="element-133" />
                    </div>
                    <div data-id="element-134">
                      <label className="block text-xs font-semibold text-gray-700 mb-2" data-id="element-135">
                        CVV
                      </label>
                      <input type="text" placeholder="123" className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-900 text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all placeholder-gray-400" data-id="element-136" />
                    </div>
                  </div>

                  <div data-id="element-137">
                    <label className="block text-xs font-semibold text-gray-700 mb-2" data-id="element-138">
                      Cardholder name
                    </label>
                    <input type="text" placeholder="George Sands" className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-900 text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all placeholder-gray-400" data-id="element-139" />
                  </div>
                </> : <>
                  <div data-id="element-140">
                    <label className="block text-xs font-semibold text-gray-700 mb-2" data-id="element-141">
                      Account holder name
                    </label>
                    <input type="text" defaultValue="George Sands" className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-900 text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all" data-id="element-142" />
                  </div>

                  <div data-id="element-143">
                    <label className="block text-xs font-semibold text-gray-700 mb-2" data-id="element-144">
                      Routing number
                    </label>
                    <input type="text" placeholder="XXXXXXXXXXXXXX" className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-900 text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all placeholder-gray-400" data-id="element-145" />
                  </div>

                  <div data-id="element-146">
                    <label className="block text-xs font-semibold text-gray-700 mb-2" data-id="element-147">
                      Account number
                    </label>
                    <input type="text" placeholder="XXXXXXXXXXX" className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-900 text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all placeholder-gray-400" data-id="element-148" />
                  </div>

                  <div data-id="element-149">
                    <label className="block text-xs font-semibold text-gray-700 mb-2" data-id="element-150">
                      Account type
                    </label>
                    <div className="relative" data-id="element-151">
                      <select className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-900 text-sm appearance-none focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all" data-id="element-152">
                        <option data-id="element-153">Checking</option>
                        <option data-id="element-154">Savings</option>
                      </select>
                      <ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={16} data-id="element-155" />
                    </div>
                  </div>
                </>}

              <p className="text-xs text-gray-500 leading-relaxed mt-4" data-id="element-156">
                By providing your{' '}
                {paymentMethod === 'card' ? 'card' : 'account'} information, you
                allow Charity Name to charge your{' '}
                {paymentMethod === 'card' ? 'card' : 'account'} for future
                payments in accordance with their terms.
              </p>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Footer Section */}
      <div className="bg-white px-8 py-6 border-t border-gray-100 rounded-b-3xl" data-id="element-157">
        <div data-id="element-158">
          <div className={`text-3xl font-bold text-gray-900 mb-1 ${alignment === 'center' ? 'text-center' : 'text-left'}`} data-id="element-159">
            ${baseAmount.toFixed(2)}
          </div>
          <AnimatePresence mode="wait" data-id="element-160">
            {(!showFeeEditor || keepFeeLineVisible) && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} className={`flex items-center text-sm text-gray-600 ${alignment === 'center' ? 'justify-center' : 'justify-start'}`} data-id="element-161">
                <span data-id="element-162">+ ${processingFee.toFixed(2)} processing fee</span>
                <button onClick={() => setShowFeeEditor(!showFeeEditor)} className="ml-1.5 text-gray-700 hover:text-gray-900 font-bold flex items-center gap-0.5 transition-colors" aria-label="Edit processing fee" data-id="element-163">
                  Edit <HelpCircle size={12} data-id="element-164" />
                </button>
              </motion.div>}
          </AnimatePresence>

          {/* Enhanced Fee Editor Section */}
          <AnimatePresence data-id="element-165">
            {showFeeEditor && <motion.div initial={{
            opacity: 0,
            height: 0,
            marginTop: 0
          }} animate={{
            opacity: 1,
            height: 'auto',
            marginTop: 16
          }} exit={{
            opacity: 0,
            height: 0,
            marginTop: 0
          }} transition={{
            duration: 0.3,
            ease: 'easeInOut'
          }} className="overflow-hidden" data-id="element-166">
                <div className="p-5 bg-gradient-to-br from-gray-50 to-white rounded-2xl border-2 border-gray-200 shadow-sm" data-id="element-167">
                  <div className="flex items-start justify-between mb-4" data-id="element-168">
                    <div data-id="element-169">
                      <p className="text-sm font-bold text-gray-900 mb-1" data-id="element-170">
                        Support our mission
                      </p>
                      <p className="text-xs text-gray-600 leading-relaxed" data-id="element-171">
                        When you increase your contribution to cover fees,
                        you're helping Innocence Project provide a safe and
                        secure giving experience while keeping processing and
                        software costs low.
                      </p>
                    </div>
                    <button onClick={() => setShowFeeEditor(false)} className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-lg" aria-label="Close fee editor" data-id="element-172">
                      <X size={16} data-id="element-173" />
                    </button>
                  </div>

                  {/* Version 2: Enhanced Slider */}
                  {feeEditorVersion === 2 && <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} className="space-y-4" data-id="element-174">
                      <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm" data-id="element-175">
                        <div className="text-center mb-4" data-id="element-176">
                          <div className="text-3xl font-bold text-gray-900 mb-1" data-id="element-177">
                            ${processingFee.toFixed(2)}
                          </div>
                        </div>

                        <div className="relative px-2" data-id="element-178">
                          <input type="range" min="0" max="3" step="1" value={getCurrentSliderIndex()} onChange={handleSliderChange} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider" style={{
                      background: `linear-gradient(to right, ${secondaryColor} 0%, ${secondaryColor} ${getCurrentSliderIndex() / 3 * 100}%, #e5e7eb ${getCurrentSliderIndex() / 3 * 100}%, #e5e7eb 100%)`,
                      ['--slider-color' as any]: secondaryColor
                    }} aria-label="Adjust processing fee" data-id="element-179" />
                        </div>
                      </div>
                    </motion.div>}

                  {/* Version 3: Enhanced Buttons */}
                  {feeEditorVersion === 3 && <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} className="grid grid-cols-4 gap-3" data-id="element-180">
                      {feeOptions.map(option => <motion.button key={option.percentage} whileHover={{
                  scale: 1.02
                }} whileTap={{
                  scale: 0.98
                }} onClick={() => setFeePercentage(option.percentage)} className={`py-2.5 px-5 rounded-full flex items-center justify-center transition-all ${feePercentage === option.percentage ? 'bg-black text-white' : 'bg-white text-gray-900 border-2 border-gray-300'}`} data-id="element-181">
                          <div className="text-base font-medium" data-id="element-182">
                            ${option.amount.toFixed(2)}
                          </div>
                        </motion.button>)}
                    </motion.div>}

                  {/* Version 4: Enhanced Dropdown */}
                  {feeEditorVersion === 4 && <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} className="relative" data-id="element-183">
                      <select value={feePercentage} onChange={e => setFeePercentage(parseFloat(e.target.value))} className="w-full px-4 py-4 bg-white text-gray-900 border-2 border-gray-200 rounded-xl text-base font-semibold appearance-none cursor-pointer focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all shadow-sm" aria-label="Select processing fee amount" data-id="element-184">
                        {feeOptions.reverse().map(option => <option key={option.percentage} value={option.percentage} data-id="element-185">
                            ${option.amount.toFixed(2)}
                          </option>)}
                      </select>
                      <ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={20} data-id="element-186" />
                    </motion.div>}
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>

        <motion.button whileHover={{
        scale: 1.01
      }} whileTap={{
        scale: 0.99
      }} onClick={handleDonate} disabled={isProcessing} className="w-full font-bold py-4 rounded-full text-lg shadow-md hover:shadow-lg transition-all transform mt-6 disabled:opacity-50 disabled:cursor-not-allowed" style={{
        backgroundColor: isProcessing ? '#9ca3af' : brandColor,
        color: brandColor === '#ccff00' || brandColor === '#CCFF00' ? '#000000' : '#ffffff'
      }} data-id="element-187">
          {isProcessing ? <span className="flex items-center justify-center gap-2" data-id="element-188">
              <motion.div animate={{
            rotate: 360
          }} transition={{
            duration: 1,
            repeat: Infinity,
            ease: 'linear'
          }} className="w-5 h-5 border-2 border-current border-t-transparent rounded-full" data-id="element-189" />
              Processing...
            </span> : `Donate $${totalAmount.toFixed(2)} CAD`}
        </motion.button>
      </div>
    </div>;
}